/**
 * MobileCalendar Component for LOOPINO AI Platform
 * Mobile weekly calendar view with swipe navigation
 */

import React, { useState, useEffect, useCallback, useRef, useMemo, useLayoutEffect } from 'react';
import './MobileCalendar.css';
import { useMobileSwipe } from '../../../hooks/useMobileSwipe';
import MobileCampaignSelector from '../MobileCampaignSelector/MobileCampaignSelector.jsx';
import useApi from '../../../hooks/useApi';
import postService from '../../../services/PostService';
import useCampaign from '../../../hooks/useCampaign';
import useLanguage from '../../../hooks/useLanguage';
import { getFirstMediaUrl, isVideoFile } from '../../../utils/mediaHelpers.js';
import { Skeleton } from '../../effects';

// Social platform icons (inline SVG)
const PlatformIcon = ({ platform, size = 14 }) => {
  const icons = {
    instagram: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="2" y="2" width="20" height="20" rx="5" ry="5" stroke="currentColor" strokeWidth="2"/>
        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" stroke="currentColor" strokeWidth="2"/>
        <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" stroke="currentColor" strokeWidth="2"/>
      </svg>
    ),
    facebook: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M18 2H15C13.6739 2 12.4021 2.52678 11.4645 3.46447C10.5268 4.40215 10 5.67392 10 7V10H7V14H10V22H14V14H17L18 10H14V7C14 6.73478 14.1054 6.48043 14.2929 6.29289C14.4804 6.10536 14.7348 6 15 6H18V2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    linkedin: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M16 8C18.5259 8 20.5259 10 20.5259 12.5V19H16.5259V12.5C16.5259 11.5 15.5259 11 14.5259 11C13.5259 11 12.5259 11.5 12.5259 12.5V19H8.52588V8H12.5259V9.5C13.5259 8.5 14.5259 8 16 8Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <rect x="2" y="9" width="4" height="12" stroke="currentColor" strokeWidth="2"/>
        <circle cx="4" cy="4" r="2" stroke="currentColor" strokeWidth="2"/>
      </svg>
    ),
    x: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4 4L20 20M20 4L4 20" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    )
  };
  
  return icons[platform] || icons.instagram;
};

// Approval icon
const CheckIcon = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <polyline points="20,6 9,17 4,12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// X icon for not approved
const XIcon = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2"/>
    <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2"/>
  </svg>
);

// Arrow icons for navigation (when needed)
const ArrowIcon = ({ direction = 'left', size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    {direction === 'left' ? (
      <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    ) : (
      <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    )}
  </svg>
);

// Clock icon for scheduled/publishing posts
const ClockIcon = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
    <path d="M12 7V12L15 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const normalizePostIdValue = (value) => {
  if (value === undefined || value === null) {
    return null;
  }

  if (typeof value === 'string' || typeof value === 'number') {
    return String(value);
  }

  if (typeof value === 'object') {
    if (value.$oid) {
      return String(value.$oid);
    }

    if (value.id || value._id) {
      return normalizePostIdValue(value.id ?? value._id);
    }

    if (typeof value.toString === 'function') {
      const stringified = value.toString();
      if (stringified && stringified !== '[object Object]') {
        return String(stringified);
      }
    }
  }

  return null;
};

const getPostIdentifier = (post) => {
  if (!post) {
    return null;
  }

  return (
    normalizePostIdValue(post.id) ||
    normalizePostIdValue(post._id) ||
    normalizePostIdValue(post.post_id) ||
    normalizePostIdValue(post.postId) ||
    null
  );
};

const getCalendarPostStatusInfo = (post) => {
  const rawStatus = post?.status;
  let normalizedStatus = '';

  if (typeof rawStatus === 'string') {
    normalizedStatus = rawStatus.toLowerCase();
  } else if (rawStatus && typeof rawStatus === 'object') {
    const candidate = rawStatus.value || rawStatus.current || rawStatus.status || rawStatus.state || rawStatus.name;
    normalizedStatus = candidate ? candidate.toString().toLowerCase() : '';
  } else if (rawStatus !== undefined && rawStatus !== null) {
    normalizedStatus = String(rawStatus).toLowerCase();
  }

  const isExplicitlyPosted = post?.posted === true;

  if (isExplicitlyPosted || normalizedStatus === 'published' || normalizedStatus === 'posted') {
    return {
      badgeVariant: 'posted',
      icon: 'check',
      cardClass: 'posted',
      isApproved: true
    };
  }

  if (normalizedStatus === 'failed') {
    return {
      badgeVariant: 'failed',
      icon: 'x',
      cardClass: 'failed',
      isApproved: false
    };
  }

  if (normalizedStatus === 'cancelled' || normalizedStatus === 'canceled') {
    return {
      badgeVariant: 'cancelled',
      icon: 'x',
      cardClass: 'cancelled',
      isApproved: false
    };
  }

  if (normalizedStatus === 'publishing' || normalizedStatus === 'processing') {
    return {
      badgeVariant: 'publishing',
      icon: 'clock',
      cardClass: 'publishing',
      isApproved: true
    };
  }

  if (normalizedStatus === 'queued' || normalizedStatus === 'scheduled') {
    return {
      badgeVariant: 'scheduled',
      icon: 'check',
      cardClass: 'scheduled',
      isApproved: true
    };
  }

  return {
    badgeVariant: 'pending',
    icon: 'x',
    cardClass: 'draft',
    isApproved: false
  };
};

const MobileCalendar = ({
  posts = [],
  onPostClick,
  targetDate = null, // Data target per navigare a una settimana specifica
  isLoading = false,
  campaigns = [],
  onCampaignSelectionChange
}) => {
  // Campaigns via hook (API-loaded, shared logic with Strategy)
  const { campaigns: apiCampaigns, loadCampaigns } = useCampaign();
  // Local campaign state
  const [selectedCampaignIds, setSelectedCampaignIds] = useState([]);
  const [isAllCampaignsSelected, setIsAllCampaignsSelected] = useState(true);
  const [currentWeek, setCurrentWeek] = useState(targetDate || new Date());
  const [currentTime, setCurrentTime] = useState(new Date());
  const [carouselOffset, setCarouselOffset] = useState(0);
  const [carouselTransition, setCarouselTransition] = useState('none');
  const [carouselAnimation, setCarouselAnimation] = useState(null);
  const [videoThumbnails, setVideoThumbnails] = useState({});
  const [thumbnailsLoading, setThumbnailsLoading] = useState({});
  const containerRef = useRef(null);
  const weekHeaderViewportRef = useRef(null);
  const [weekViewportWidth, setWeekViewportWidth] = useState(0);
  const [showCampaignSelector, setShowCampaignSelector] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [pickerMonth, setPickerMonth] = useState(new Date());
  const [calendarPosts, setCalendarPosts] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarError, setCalendarError] = useState(null);
  const api = useApi();
  const serviceInitializedRef = useRef(false);
  const calendarCacheRef = useRef(new Map());
  const lastSwipeOffsetRef = useRef(0);
  const isNavigatingRef = useRef(false);
  const { t, language } = useLanguage();

  const renderLoadingSkeleton = () => {
    const placeholderDays = Array.from({ length: 7 });
    const placeholderRows = Array.from({ length: 4 });

    return (
      <div className="mobile-calendar-container">
        <div className="mobile-calendar-grid">
          <div className="mobile-calendar-week-navigation">
            <div className="mobile-calendar-week-nav-left">
              <Skeleton variant="circle" width={32} height={32} shimmer />
              <Skeleton variant="rounded" width={72} height={28} shimmer />
              <Skeleton variant="circle" width={32} height={32} shimmer />
            </div>
            <div className="mobile-calendar-week-info">
              <Skeleton width={180} height={18} shimmer />
            </div>
            <div className="mobile-calendar-week-nav-right">
              <Skeleton variant="rounded" width={40} height={36} shimmer />
            </div>
          </div>

          <div className="mobile-calendar-days-header">
            <div className="mobile-calendar-time-column-header">
              <Skeleton width="60%" height={14} shimmer />
            </div>
            <div className="mobile-calendar-week-header-viewport">
              <div className="mobile-calendar-header-track mobile-carousel-track">
                <div className="mobile-calendar-day-group">
                  {placeholderDays.map((_, dayIndex) => (
                    <div key={`loading-day-${dayIndex}`} className="mobile-calendar-day-header">
                      <div className="mobile-calendar-day-name">
                        <Skeleton width="50%" height={12} shimmer />
                      </div>
                      <div className="mobile-calendar-day-number">
                        <Skeleton width={28} height={18} shimmer />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="mobile-calendar-body">
            {placeholderRows.map((_, rowIndex) => (
              <div key={`loading-row-${rowIndex}`} className="mobile-calendar-time-row has-posts">
                <div className="mobile-calendar-time-label">
                  <Skeleton width={48} height={14} shimmer />
                </div>
                <div className="mobile-calendar-week-row-viewport">
                  <div className="mobile-calendar-row-track mobile-carousel-track">
                    <div className="mobile-calendar-day-group">
                      {placeholderDays.map((_, dayIndex) => (
                        <div key={`loading-cell-${rowIndex}-${dayIndex}`} className="mobile-calendar-cell has-posts">
                          <Skeleton variant="rounded" width="100%" height={64} shimmer />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const resolvedLocale = useMemo(() => {
    if (!language) {
      return 'it-IT';
    }

    if (language.includes('-')) {
      return language;
    }

    if (language === 'it') {
      return 'it-IT';
    }

    if (language === 'en') {
      return 'en-US';
    }

    return language;
  }, [language]);

  const weekdayFormatter = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(resolvedLocale, { weekday: 'short' });
    } catch (error) {
      console.warn('[MobileCalendar] Falling back to default weekday formatter', error);
      return new Intl.DateTimeFormat('it-IT', { weekday: 'short' });
    }
  }, [resolvedLocale]);

  const monthYearFormatter = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(resolvedLocale, { month: 'long', year: 'numeric' });
    } catch (error) {
      console.warn('[MobileCalendar] Falling back to default month-year formatter', error);
      return new Intl.DateTimeFormat('it-IT', { month: 'long', year: 'numeric' });
    }
  }, [resolvedLocale]);

  const dayMonthFormatter = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(resolvedLocale, { day: 'numeric', month: 'short' });
    } catch (error) {
      console.warn('[MobileCalendar] Falling back to default day-month formatter', error);
      return new Intl.DateTimeFormat('it-IT', { day: 'numeric', month: 'short' });
    }
  }, [resolvedLocale]);

  const dayMonthYearFormatter = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(resolvedLocale, { day: 'numeric', month: 'short', year: 'numeric' });
    } catch (error) {
      console.warn('[MobileCalendar] Falling back to default day-month-year formatter', error);
      return new Intl.DateTimeFormat('it-IT', { day: 'numeric', month: 'short', year: 'numeric' });
    }
  }, [resolvedLocale]);

  const formatWeekdayLabel = useCallback((date) => {
    const formatted = weekdayFormatter.format(date);
    if (!formatted) {
      return '';
    }
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  }, [weekdayFormatter]);

  const datePickerWeekdays = useMemo(() => {
    const localized = t('calendar.datePicker.weekdays', { returnObjects: true });
    if (Array.isArray(localized) && localized.length === 7) {
      return localized;
    }

    return resolvedLocale.startsWith('en')
      ? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
      : ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
  }, [resolvedLocale, t]);

  useLayoutEffect(() => {
    if (!weekHeaderViewportRef.current) {
      return;
    }

    const element = weekHeaderViewportRef.current;

    const updateWidth = () => {
      const rect = element.getBoundingClientRect();
      setWeekViewportWidth(rect.width);
    };

    updateWidth();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(updateWidth);
      observer.observe(element);

      return () => {
        observer.disconnect();
      };
    }

    window.addEventListener('resize', updateWidth);
    return () => {
      window.removeEventListener('resize', updateWidth);
    };
  }, []);

  // Handle campaign selection changes
  const handleCampaignSelectionChange = useCallback((newSelectedIds, selectionType) => {
    setSelectedCampaignIds(newSelectedIds);
    // Tratta "tutte le campagne" del selettore come selezione di tutte quelle visibili,
    // non come modalità globale: se l'elenco è vuoto, allora è davvero "tutte" (nessun filtro)
    setIsAllCampaignsSelected(newSelectedIds.length === 0);
    
    // Call parent callback if provided
    if (onCampaignSelectionChange) {
      onCampaignSelectionChange(newSelectedIds, selectionType);
    }
  }, [onCampaignSelectionChange]);

  // Update current week when targetDate changes
  useEffect(() => {
    if (targetDate) {
      console.log('[MobileCalendar] Setting target week to:', targetDate);
      setCurrentWeek(targetDate);
    }
  }, [targetDate]);

  // Initialize PostService with API hook once
  useEffect(() => {
    if (!serviceInitializedRef.current && api) {
      try {
        postService.initialize(api);
        serviceInitializedRef.current = true;
        // Carica campagne via hook per avere la stessa logica della Strategy
        loadCampaigns({ limit: 200 }).catch((err) => {
          console.error('[MobileCalendar] Failed to load campaigns via hook:', err);
        });
      } catch (e) {
        console.error('[MobileCalendar] Failed to initialize services', e);
      }
    }
  }, [api, loadCampaigns]);

  // Compute current week range (Mon-Sun)
  const getWeekRange = useCallback((date) => {
    const weekDays = [];
    const start = new Date(date);
    const day = start.getDay();
    const diff = start.getDate() - day + (day === 0 ? -6 : 1);
    start.setDate(diff);
    for (let i = 0; i < 7; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      weekDays.push(d);
    }
    const startDate = weekDays[0];
    const endDate = weekDays[6];
    const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return { start_date: fmt(startDate), end_date: fmt(endDate) };
  }, []);

  // Load posts for the calendar (all posts, filtered client-side by week)
  const loadCalendarPosts = useCallback(async () => {
    if (!serviceInitializedRef.current) return;
    setCalendarLoading(true);
    setCalendarError(null);
    try {
      // Build cache key based on campaign selection only (not week, since we load all posts)
      const key = () => {
        const selection = (isAllCampaignsSelected || selectedCampaignIds.length === 0)
          ? 'all'
          : selectedCampaignIds.slice().sort().join(',');
        return `calendar_posts|${selection}`;
      };
      const cacheKey = key();

      // Use cached immediately if present
      if (calendarCacheRef.current.has(cacheKey)) {
        const cached = calendarCacheRef.current.get(cacheKey);
        if (Array.isArray(cached)) {
          console.log('[MobileCalendar] Using cached posts:', cached.length);
          setCalendarPosts(cached);
          setCalendarLoading(false);
          return;
        }
      }

      // Helper to fetch all posts with pagination
      const fetchAllForParams = async (params) => {
        const pageLimit = 1000;
        let offset = 0;
        let all = [];
        // Safety cap to avoid infinite loops
        const maxIterations = 50; // Increased to support more posts
        let iter = 0;
        // eslint-disable-next-line no-constant-condition
        while (true) {
          console.log('[MobileCalendar] Fetching posts batch:', { offset, pageLimit, params });
          const resp = await postService.listPosts({ ...params, limit: pageLimit, offset });
          const items = resp.posts || [];
          all = all.concat(items);
          const hasNext = resp.pagination?.has_next || items.length === pageLimit;
          offset += items.length;
          iter += 1;
          console.log('[MobileCalendar] Batch fetched:', { items: items.length, total: all.length, hasNext });
          if (!hasNext || iter >= maxIterations) break;
        }
        return all;
      };

      // Decide campaign filters - IMPORTANT: API does NOT support start_date/end_date filters
      // We load ALL posts and filter client-side for the current week
      const hasSelection = selectedCampaignIds.length > 0;
      let merged = [];

      if (!hasSelection) {
        // Load all posts for all campaigns
        console.log('[MobileCalendar] Loading all posts (no campaign filter)');
        merged = await fetchAllForParams({});
      } else if (selectedCampaignIds.length === 1) {
        // Single campaign - use campaign_id filter
        console.log('[MobileCalendar] Loading posts for single campaign:', selectedCampaignIds[0]);
        merged = await fetchAllForParams({ campaign_id: selectedCampaignIds[0] });
      } else {
        // Multiple campaigns: fetch per-campaign for better performance
        const MULTI_FETCH_THRESHOLD = 10;
        if (selectedCampaignIds.length <= MULTI_FETCH_THRESHOLD) {
          console.log('[MobileCalendar] Loading posts for multiple campaigns:', selectedCampaignIds.length);
          const promises = selectedCampaignIds.map((cid) => fetchAllForParams({ campaign_id: cid }));
          const results = await Promise.all(promises);
          const byId = new Map();
          results.flat().forEach((p) => {
            const pid = p.id || p._id;
            if (!byId.has(pid)) byId.set(pid, p);
          });
          merged = Array.from(byId.values());
        } else {
          // Too many campaigns - load all and filter client-side
          console.log('[MobileCalendar] Loading all posts (too many campaigns selected)');
          merged = await fetchAllForParams({});
        }
      }

      console.log('[MobileCalendar] Total posts loaded:', merged.length);
      console.log('[MobileCalendar] Sample posts:', merged.slice(0, 5).map(p => ({
        id: p.id,
        campaign_id: p.campaign_id || p.campaignId,
        scheduled_time: p.scheduled_time || p.scheduledDate,
        content: (p.content || p.description || '').substring(0, 50)
      })));

      // Sort by scheduled_time ascending
      merged.sort((a, b) => {
        const da = new Date(a.scheduledDate || a.scheduled_time || 0).getTime();
        const db = new Date(b.scheduledDate || b.scheduled_time || 0).getTime();
        return da - db;
      });

      setCalendarPosts(merged);
      // Update cache
      calendarCacheRef.current.set(cacheKey, merged);
    } catch (err) {
      console.error('[MobileCalendar] Failed to load posts:', err);
      setCalendarError(err?.message || t('calendar.errors.loadPosts'));
    } finally {
      setCalendarLoading(false);
    }
  }, [isAllCampaignsSelected, selectedCampaignIds, t]);

  // Fetch when week changes or after initialization
  useEffect(() => {
    loadCalendarPosts();
  }, [loadCalendarPosts]);

  // Sync updates coming from other views (approve/schedule/edit)
  useEffect(() => {
    const handlePostUpdated = (e) => {
      const { postId } = e.detail || {};
      if (!postId) return;

      console.log('[MobileCalendar] Post updated event received, reloading calendar data for postId:', postId);

      // Clear the entire cache to force a fresh reload
      calendarCacheRef.current.clear();

      // Reload the calendar posts from the backend to get the most up-to-date data
      // This ensures we get the correct scheduled_time and all other fields
      loadCalendarPosts();
    };

    const handlePostDirectUpdate = (e) => {
      // Same logic as handlePostUpdated
      handlePostUpdated(e);
    };

    window.addEventListener('postUpdated', handlePostUpdated);
    window.addEventListener('postDirectUpdate', handlePostDirectUpdate);
    return () => {
      window.removeEventListener('postUpdated', handlePostUpdated);
      window.removeEventListener('postDirectUpdate', handlePostDirectUpdate);
    };
  }, [loadCalendarPosts]);

  // Determine source posts (prefer internal fetched posts)
  const sourcePosts = (calendarPosts && calendarPosts.length > 0) ? calendarPosts : posts;

  // Filter posts based on selected campaigns AND current week
  const filteredPosts = useCallback(() => {
    let filtered = sourcePosts;

    // Filter by campaign if campaigns are selected
    if (selectedCampaignIds.length > 0) {
      const selectedIdSet = new Set(selectedCampaignIds.map(id => String(id)));

      console.log('[MobileCalendar] Filtering by campaigns:', {
        selectedIds: Array.from(selectedIdSet),
        totalPosts: sourcePosts.length,
        postCampaignIds: sourcePosts.map(p => {
          const rawId = p.campaign_id || p.campaignId;
          let extractedId = rawId;

          // Extract ID from ObjectId format
          if (rawId && typeof rawId === 'object') {
            if (rawId.$oid) {
              extractedId = rawId.$oid;
            } else if (rawId.toString) {
              extractedId = rawId.toString();
            }
          }

          return {
            postId: p.id,
            rawCampaignId: rawId,
            extractedCampaignId: String(extractedId || ''),
            isObject: typeof rawId === 'object'
          };
        })
      });

      filtered = filtered.filter(post => {
        // Extract campaign_id - handle both string and ObjectId formats
        let postCampaignId = post.campaign_id || post.campaignId;

        // Handle MongoDB ObjectId format: { $oid: "..." } or ObjectId object with toString()
        if (postCampaignId && typeof postCampaignId === 'object') {
          if (postCampaignId.$oid) {
            postCampaignId = postCampaignId.$oid;
          } else if (postCampaignId.toString && typeof postCampaignId.toString === 'function') {
            postCampaignId = postCampaignId.toString();
          } else {
            console.warn('[MobileCalendar] Unknown campaign_id format:', postCampaignId);
            postCampaignId = '';
          }
        }

        // Convert to string for comparison
        postCampaignId = String(postCampaignId || '');

        const matches = selectedIdSet.has(postCampaignId);

        if (!matches) {
          console.log('[MobileCalendar] Post filtered out:', {
            postId: post.id,
            postCampaignId,
            rawCampaignId: post.campaign_id || post.campaignId,
            selectedIds: Array.from(selectedIdSet),
            reason: 'campaign_id not in selected set'
          });
        }

        return matches;
      });
    }

    // Filter by current week (client-side since API doesn't support date range)
    const { start_date, end_date } = getWeekRange(currentWeek);
    const weekStart = new Date(start_date);
    const weekEnd = new Date(end_date);
    weekEnd.setHours(23, 59, 59, 999); // End of day

    filtered = filtered.filter(post => {
      const postDate = new Date(post.scheduledDate || post.scheduled_time);
      // Only include posts with valid dates in the current week
      if (!postDate || isNaN(postDate.getTime())) return false;
      return postDate >= weekStart && postDate <= weekEnd;
    });

    console.log('[MobileCalendar] Posts filtered:', {
      total: sourcePosts.length,
      selectedCampaignIds: selectedCampaignIds,
      isAllCampaigns: selectedCampaignIds.length === 0,
      afterCampaignFilter: filtered.length,
      weekStart: weekStart.toISOString(),
      weekEnd: weekEnd.toISOString(),
      finalCount: filtered.length,
      samplePosts: filtered.slice(0, 3).map(p => ({
        id: p.id,
        campaign_id: p.campaign_id || p.campaignId,
        scheduled_time: p.scheduled_time || p.scheduledDate
      }))
    });

    return filtered;
  }, [sourcePosts, selectedCampaignIds, currentWeek, getWeekRange]);

  const displayPosts = filteredPosts();


  // Helper function to generate thumbnail from video first frame
  const generateVideoThumbnail = useCallback((videoUrl) => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      video.crossOrigin = 'anonymous';
      video.muted = true;
      video.playsInline = true;
      
      const handleLoadedData = () => {
        try {
          // Set canvas size for calendar thumbnails (small size)
          const aspectRatio = video.videoWidth / video.videoHeight;
          canvas.width = 60; // Fixed thumbnail width for calendar
          canvas.height = 60 / aspectRatio;
          
          // Draw first frame to canvas
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          
          // Convert canvas to data URL
          const thumbnailDataUrl = canvas.toDataURL('image/jpeg', 0.8);
          resolve(thumbnailDataUrl);
        } catch (error) {
          reject(error);
        } finally {
          // Cleanup
          video.removeEventListener('loadeddata', handleLoadedData);
          video.removeEventListener('error', handleError);
          video.src = '';
          video.load();
        }
      };
      
      const handleError = (error) => {
        video.removeEventListener('loadeddata', handleLoadedData);
        video.removeEventListener('error', handleError);
        reject(new Error('Failed to load video for thumbnail generation'));
      };
      
      video.addEventListener('loadeddata', handleLoadedData);
      video.addEventListener('error', handleError);
      
      // Load video and seek to first frame
      video.src = videoUrl;
      video.currentTime = 0.1; // Slightly after start to ensure frame is loaded
    });
  }, []);

  // Generate thumbnails for video files in posts
  useEffect(() => {
    const generateThumbnails = async () => {
      if (!posts || posts.length === 0) return;
      
      for (const post of posts) {
        const originalThumbnail = getFirstMediaUrl(post);
        if (!originalThumbnail) continue;
        
        const thumbnailKey = `${post.id}-${originalThumbnail}`;
        
        if (isVideoFile(originalThumbnail) && !videoThumbnails[thumbnailKey] && !thumbnailsLoading[thumbnailKey]) {
          setThumbnailsLoading(prev => ({ ...prev, [thumbnailKey]: true }));
          
          try {
            const thumbnail = await generateVideoThumbnail(originalThumbnail);
            setVideoThumbnails(prev => ({ ...prev, [thumbnailKey]: thumbnail }));
          } catch (error) {
            console.error('[MobileCalendar] Error generating video thumbnail:', error);
          } finally {
            setThumbnailsLoading(prev => ({ ...prev, [thumbnailKey]: false }));
          }
        }
      }
    };
    
    generateThumbnails();
  }, [posts, generateVideoThumbnail, videoThumbnails, thumbnailsLoading]);

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  // Auto-scroll to 8:00 AM when calendar opens
  useEffect(() => {
    const scrollToDefaultHour = () => {
      const targetHour = 8;
      const timeRows = containerRef.current?.querySelectorAll('.mobile-calendar-time-row');
      
      if (timeRows && timeRows.length > 0) {
        // Find the time row for 8:00
        const targetRowIndex = targetHour - 1; // Hours start from 1, so 8:00 is at index 7
        const targetRow = timeRows[targetRowIndex];
        
        if (targetRow && containerRef.current) {
          const container = containerRef.current.querySelector('.mobile-calendar-body');
          if (container) {
            // Calculate scroll position to center the 8:00 row
            const containerHeight = container.clientHeight;
            const rowOffsetTop = targetRow.offsetTop;
            const rowHeight = targetRow.clientHeight;
            const scrollPosition = Math.max(0, rowOffsetTop - (containerHeight / 2) + (rowHeight / 2));
            
            // Smooth scroll to position
            container.scrollTo({
              top: scrollPosition,
              behavior: 'smooth'
            });
          }
        }
      }
    };

    // Delay scroll to ensure component is fully rendered
    const timer = setTimeout(scrollToDefaultHour, 100);
    return () => clearTimeout(timer);
  }, [currentWeek]); // Re-scroll when week changes

  // Get week days (Monday to Sunday)
  const getWeekDays = (date) => {
    const week = [];
    const startOfWeek = new Date(date);
    const day = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
    startOfWeek.setDate(diff);

    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      week.push(day);
    }
    return week;
  };

  // Generate hours (1 AM to 24/midnight)
  const generateHours = () => {
    const hours = [];
    for (let i = 1; i <= 24; i++) {
      hours.push(i);
    }
    return hours;
  };

  // Check if date is today
  const isToday = useCallback((date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  }, []);

  // Get posts for specific time slot
  const getPostsForTimeSlot = useCallback((date, hour) => {
    return displayPosts.filter(post => {
      const postDate = new Date(post.scheduledDate || post.scheduled_time || Date.now());
      const postHour = postDate.getHours();
      // Convert calendar hour (1-24) to standard hour (0-23)
      const standardHour = hour === 24 ? 0 : hour;
      return postDate.toDateString() === date.toDateString() && postHour === standardHour;
    });
  }, [displayPosts]);

  const previousWeekDays = useMemo(() => {
    const previousWeek = new Date(currentWeek);
    previousWeek.setDate(previousWeek.getDate() - 7);
    return getWeekDays(previousWeek);
  }, [currentWeek]);

  const nextWeekDays = useMemo(() => {
    const upcomingWeek = new Date(currentWeek);
    upcomingWeek.setDate(upcomingWeek.getDate() + 7);
    return getWeekDays(upcomingWeek);
  }, [currentWeek]);

  const animateToWeek = useCallback((direction, startOffset = 0) => {
    if (carouselAnimation) return;

    const viewportWidth = weekViewportWidth || (typeof window !== 'undefined' ? window.innerWidth : 0);
    if (!viewportWidth) return;

    const targetWeek = new Date(currentWeek);
    targetWeek.setDate(targetWeek.getDate() + (direction * 7));

    const boundedOffset = Math.max(-viewportWidth, Math.min(viewportWidth, startOffset));

    isNavigatingRef.current = true;
    setCarouselAnimation({ type: 'navigate', direction, targetWeek });
    setCarouselTransition('none');
    setCarouselOffset(boundedOffset);

    const targetOffset = direction === 1 ? -viewportWidth : viewportWidth;

    requestAnimationFrame(() => {
      setCarouselTransition('transform 0.35s ease');
      setCarouselOffset(targetOffset);
    });
    lastSwipeOffsetRef.current = 0;
  }, [carouselAnimation, currentWeek, weekViewportWidth, lastSwipeOffsetRef]);

  const animateReturnToCenter = useCallback((startOffset = 0) => {
    const viewportWidth = weekViewportWidth || (typeof window !== 'undefined' ? window.innerWidth : 0);
    if (!viewportWidth) return;

    const boundedOffset = Math.max(-viewportWidth, Math.min(viewportWidth, startOffset));

    isNavigatingRef.current = true;
    setCarouselAnimation({ type: 'return' });
    setCarouselTransition('none');
    setCarouselOffset(boundedOffset);

    requestAnimationFrame(() => {
      setCarouselTransition('transform 0.25s ease');
      setCarouselOffset(0);
    });
    lastSwipeOffsetRef.current = 0;
  }, [weekViewportWidth, lastSwipeOffsetRef]);

  // Navigate to current week (today)
  const navigateToToday = useCallback(() => {
    if (carouselAnimation) return;

    isNavigatingRef.current = false;
    setCurrentWeek(new Date());
    setCarouselOffset(0);
    setCarouselTransition('none');
  }, [carouselAnimation]);

  // Check if we're in the current week
  const isCurrentWeek = useCallback(() => {
    const today = new Date();
    const todayWeekDays = getWeekDays(today);
    const currentWeekDays = getWeekDays(currentWeek);

    // Compare start dates of both weeks
    return todayWeekDays[0].toDateString() === currentWeekDays[0].toDateString();
  }, [currentWeek]);

  const navigateWeek = useCallback((direction) => {
    animateToWeek(direction, 0);
  }, [animateToWeek]);

  const handleSwipeLeft = useCallback(() => {
    animateToWeek(1, lastSwipeOffsetRef.current);
  }, [animateToWeek, lastSwipeOffsetRef]);

  const handleSwipeRight = useCallback(() => {
    animateToWeek(-1, lastSwipeOffsetRef.current);
  }, [animateToWeek, lastSwipeOffsetRef]);

  // Handle post click
  const handlePostClick = useCallback((post) => {
    if (onPostClick) {
      let postIndex = -1;
      const postId = getPostIdentifier(post);

      if (postId && sourcePosts && sourcePosts.length > 0) {
        postIndex = sourcePosts.findIndex(p => getPostIdentifier(p) === postId);
      }

      if (postIndex === -1 && postId && posts && posts.length > 0) {
        postIndex = posts.findIndex(p => getPostIdentifier(p) === postId);
      }

      console.log('[MobileCalendar] Post clicked:', postId, 'found at index:', postIndex);
      onPostClick(post, postIndex, postId);
    }
  }, [sourcePosts, posts, onPostClick]);

  const renderDayHeaderGroup = useCallback((days, keyPrefix) => (
    <div className="mobile-calendar-day-group" key={`header-${keyPrefix}`}>
      {days.map((day) => (
        <div
          key={`${keyPrefix}-${day.toISOString()}`}
          className={`mobile-calendar-day-header ${isToday(day) ? 'today' : ''}`}
        >
          <div className="mobile-calendar-day-name">
            {formatWeekdayLabel(day)}
          </div>
          <div className="mobile-calendar-day-number">
            {day.getDate()}
          </div>
          {isToday(day) && <div className="mobile-calendar-today-indicator"></div>}
        </div>
      ))}
    </div>
  ), [formatWeekdayLabel, isToday]);

  const renderRowCells = useCallback((days, hour, keyPrefix, cachedPostsPerDay = null) => {
    const postsPerDay = cachedPostsPerDay || days.map((day) => getPostsForTimeSlot(day, hour));

    return (
      <div className="mobile-calendar-day-group" key={`row-${keyPrefix}`}>
        {postsPerDay.map((timeSlotPosts, index) => {
          const day = days[index];
          const standardHour = hour === 24 ? 0 : hour;
          const isCurrentHour = isToday(day) && standardHour === currentTime.getHours();

          return (
            <div
              key={`${keyPrefix}-${day.toISOString()}`}
              className={`mobile-calendar-cell ${isCurrentHour ? 'current-hour' : ''} ${timeSlotPosts.length > 0 ? 'has-posts' : ''}`}
            >
              {timeSlotPosts.map(post => {
                const platform = post.platform || post.social_platform || 'instagram';
                const statusInfo = getCalendarPostStatusInfo(post);
                const originalThumbnail = getFirstMediaUrl(post);

                if (process.env.NODE_ENV === 'development') {
                  console.log('[MobileCalendar] Post thumbnail:', {
                    postId: post.id,
                    media_urls: post?.media_urls,
                    images: post?.images,
                    post_image_url: post?.post_image_url,
                    image_url: post?.image_url,
                    selectedThumbnail: originalThumbnail
                  });
                }

                const thumbnailKey = `${post.id}-${originalThumbnail}`;
                const thumbnail = isVideoFile(originalThumbnail)
                  ? videoThumbnails[thumbnailKey] || originalThumbnail
                  : originalThumbnail;
                const isLoadingThumbnail = isVideoFile(originalThumbnail) && thumbnailsLoading[thumbnailKey];

                const postDate = new Date(post.scheduledDate || post.scheduled_time || Date.now());
                const isInPast = postDate < new Date();
                const isPosted = post.posted === true;
                const isNotPosted = post.posted === false;

                const badgeVariant = (() => {
                  if (isPosted) return 'posted';

                  if (isInPast && isNotPosted) {
                    if (['pending', 'scheduled', 'publishing'].includes(statusInfo.badgeVariant)) {
                      return 'failed';
                    }
                    return statusInfo.badgeVariant;
                  }

                  return statusInfo.badgeVariant;
                })();

                const badgeClasses = `approval-status ${badgeVariant}`;

                const postClasses = (() => {
                  const classes = ['mobile-post-card'];

                  if (statusInfo.cardClass) {
                    classes.push(statusInfo.cardClass);
                  }

                  if (isInPast) {
                    if (isPosted) {
                      classes.push('posted');
                    } else if (isNotPosted) {
                      classes.push('not-posted');
                    }
                  }

                  return classes.join(' ');
                })();

                const renderStatusIcon = () => {
                  if (badgeVariant === 'scheduled' || badgeVariant === 'posted') {
                    return <CheckIcon size={10} />;
                  }

                  if (badgeVariant === 'publishing') {
                    return <ClockIcon size={10} />;
                  }

                  return <XIcon size={10} />;
                };

                return (
                  <div
                    key={post.id}
                    className={postClasses}
                    onClick={() => handlePostClick(post)}
                  >
                    {thumbnail && (
                      <div className="mobile-post-thumbnail mobile-calendar-thumbnail">
                        {isLoadingThumbnail ? (
                          <div className="mobile-calendar-thumbnail-loading">
                            <div className="mobile-calendar-thumbnail-loading-spinner" />
                          </div>
                        ) : (
                          <img
                            src={thumbnail}
                            alt={t('calendar.alt.postPreview')}
                            className="mobile-calendar-thumbnail-image"
                          />
                        )}
                        {isVideoFile(originalThumbnail) && !isLoadingThumbnail && (
                          <div className="mobile-calendar-video-indicator">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <polygon points="5,3 19,12 5,21" fill="white" stroke="white" strokeWidth="2"/>
                            </svg>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="mobile-post-meta mobile-post-meta-row">
                      <div className="mobile-calendar-platform-icon">
                        <PlatformIcon platform={platform} size={12} />
                      </div>

                      <div className={badgeClasses}>
                        {renderStatusIcon()}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  }, [currentTime, getPostsForTimeSlot, handlePostClick, isToday, thumbnailsLoading, t, videoThumbnails]);

  // Initialize swipe
  const swipeProps = useMobileSwipe(
    handleSwipeLeft,
    handleSwipeRight,
    {
      threshold: 80,
      velocityThreshold: 0.5,
      // Disabilita lo swipe della settimana quando la modale campagne è aperta
      disabled: showCampaignSelector,
      // Affidiamoci a touch-action CSS per gestire lo scroll verticale evitando gli errori dei listener passive
      preventDefaultTouchmove: false,
      onSwipeStart: () => {
        if (carouselAnimation) {
          isNavigatingRef.current = true;
        }
      },
      onSwipeUpdate: (state) => {
        if (state?.isHorizontalSwipe) {
          lastSwipeOffsetRef.current = state.swipeOffset;
        }
      },
      onSwipeEnd: () => {
        if (carouselAnimation || isNavigatingRef.current) {
          lastSwipeOffsetRef.current = 0;
          return;
        }

        const finalOffset = lastSwipeOffsetRef.current;
        if (Math.abs(finalOffset) > 6) {
          animateReturnToCenter(finalOffset);
        } else {
          setCarouselAnimation(null);
          setCarouselTransition('none');
          setCarouselOffset(0);
          lastSwipeOffsetRef.current = 0;
          isNavigatingRef.current = false;
        }
      }
    }
  );

  const {
    onTouchStart: swipeTouchStart,
    onTouchMove: swipeTouchMove,
    onTouchEnd: swipeTouchEnd,
    isDragging: isSwipeDragging,
    swipeOffset
  } = swipeProps;

  const handleTouchStart = useCallback((event) => {
    if (carouselAnimation || showCampaignSelector) return;
    swipeTouchStart?.(event);
  }, [carouselAnimation, showCampaignSelector, swipeTouchStart]);

  const handleTouchMove = useCallback((event) => {
    if (carouselAnimation || showCampaignSelector) return;
    swipeTouchMove?.(event);
  }, [carouselAnimation, showCampaignSelector, swipeTouchMove]);

  const handleTouchEnd = useCallback((event) => {
    swipeTouchEnd?.(event);
  }, [swipeTouchEnd]);

  useEffect(() => {
    if (isSwipeDragging) {
      lastSwipeOffsetRef.current = swipeOffset;
    }
  }, [isSwipeDragging, swipeOffset]);

  const handleCarouselTransitionEnd = useCallback((event) => {
    if (event.propertyName !== 'transform') return;
    if (!carouselAnimation) return;

    if (carouselAnimation.type === 'navigate') {
      setCurrentWeek(carouselAnimation.targetWeek);
    }

    setCarouselAnimation(null);
    setCarouselTransition('none');
    setCarouselOffset(0);
    lastSwipeOffsetRef.current = 0;
    isNavigatingRef.current = false;
  }, [carouselAnimation]);

  // Get current week data
  const weekDays = getWeekDays(currentWeek);
  const hours = generateHours();
  const trackStyle = useMemo(() => {
    const swipeActive = isSwipeDragging && !carouselAnimation;
    const rawOffset = swipeActive ? swipeOffset : carouselOffset;
    const offset = Number.isFinite(rawOffset) ? rawOffset : 0;

    return {
      transform: `translateX(calc(-100% + ${offset}px))`,
      transition: swipeActive ? 'none' : carouselTransition
    };
  }, [carouselAnimation, carouselOffset, carouselTransition, isSwipeDragging, swipeOffset]);

  const controlsDisabled = Boolean(carouselAnimation) || isSwipeDragging;

  // Format week range for display
  const formattedWeekRange = useMemo(() => {
    if (!weekDays || weekDays.length < 2) {
      return '';
    }

    const start = weekDays[0];
    const end = weekDays[6];

    if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
      return t('calendar.week.rangeSameMonth', {
        startDay: start.getDate(),
        endDay: end.getDate(),
        month: monthYearFormatter.format(start)
      });
    }

    return t('calendar.week.rangeDifferentMonth', {
      start: dayMonthFormatter.format(start),
      end: dayMonthYearFormatter.format(end)
    });
  }, [dayMonthFormatter, dayMonthYearFormatter, monthYearFormatter, t, weekDays]);

  // Date picker helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    // Add empty slots for days before the first day of the month
    for (let i = 0; i < (startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1); i++) {
      days.push(null);
    }
    // Add all days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  };

  const navigatePickerMonth = (direction) => {
    const newMonth = new Date(pickerMonth);
    newMonth.setMonth(newMonth.getMonth() + direction);
    setPickerMonth(newMonth);
  };

  const handleDateSelect = (date) => {
    if (!date || carouselAnimation) return;

    setShowDatePicker(false);
    setCurrentWeek(date);
  };

  const isDateInCurrentWeek = (date) => {
    if (!date) return false;
    const weekStart = weekDays[0];
    const weekEnd = weekDays[6];
    return date >= weekStart && date <= weekEnd;
  };

  const openDatePicker = () => {
    setPickerMonth(currentWeek); // Start picker on current displayed week
    setShowDatePicker(true);
  };

  // Loading state
  if (isLoading || calendarLoading) {
    return renderLoadingSkeleton();
  }

  // Error or Empty state
  if (calendarError) {
    return (
      <div className="mobile-calendar-container">
        <div className="mobile-calendar-loading">
          <div className="mobile-calendar-loading-text">{calendarError}</div>
          <button className="mobile-calendar-week-nav-button" onClick={loadCalendarPosts}>
            {t('calendar.retry')}
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="mobile-calendar-container" ref={containerRef} onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
        {/* Calendar Grid */}
        <div className="mobile-calendar-grid">
          {/* Week Navigation - Centrato nella griglia */}
          <div className="mobile-calendar-week-navigation">
            <div className="mobile-calendar-week-nav-left">
              <button
                className="mobile-calendar-week-nav-button"
                onClick={() => navigateWeek(-1)}
                disabled={controlsDisabled}
              >
                <ArrowIcon direction="left" size={16} />
              </button>

              <button
                className={`mobile-calendar-today-button ${isCurrentWeek() ? 'current' : ''}`}
                onClick={navigateToToday}
                disabled={controlsDisabled || isCurrentWeek()}
                title={t('calendar.actions.goToday')}
              >
                <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="4" cy="4" r="4" fill="currentColor"/>
                </svg>
              </button>

              <button
                className="mobile-calendar-week-nav-button"
                onClick={() => navigateWeek(1)}
                disabled={controlsDisabled}
              >
                <ArrowIcon direction="right" size={16} />
              </button>
            </div>
            
            <div
              className="mobile-calendar-week-info"
              onClick={openDatePicker}
            >
              <span className="mobile-calendar-week-range">{formattedWeekRange}</span>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginLeft: '6px', opacity: 0.6 }}>
                <polyline points="6,9 12,15 18,9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            
            <div className="mobile-calendar-week-nav-right">
              <button 
                className="mobile-calendar-campaign-selector-button"
                onClick={() => setShowCampaignSelector(true)}
                title={t('calendar.actions.selectCampaigns')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="3" width="7" height="7" rx="1" fill="currentColor" opacity="0.7"/>
                  <rect x="14" y="3" width="7" height="7" rx="1" fill="currentColor" opacity="0.9"/>
                  <rect x="3" y="14" width="7" height="7" rx="1" fill="currentColor" opacity="0.9"/>
                  <rect x="14" y="14" width="7" height="7" rx="1" fill="currentColor" opacity="0.7"/>
                </svg>
                {selectedCampaignIds.length > 0 && (
                  <span className="mobile-calendar-campaign-count">{selectedCampaignIds.length}</span>
                )}
              </button>
            </div>
          </div>

          {/* Days Header */}
          <div className="mobile-calendar-days-header">
            <div className="mobile-calendar-time-column-header">{t('calendar.labels.time')}</div>
            <div className="mobile-calendar-week-header-viewport" ref={weekHeaderViewportRef}>
              <div
                className="mobile-calendar-header-track mobile-carousel-track"
                style={trackStyle}
                onTransitionEnd={handleCarouselTransitionEnd}
              >
                {renderDayHeaderGroup(previousWeekDays, 'previous')}
                {renderDayHeaderGroup(weekDays, 'current')}
                {renderDayHeaderGroup(nextWeekDays, 'next')}
              </div>
            </div>
          </div>

          {/* Calendar Body with Time Rows */}
          <div className="mobile-calendar-body">
            {hours.map(hour => {
              const currentRowPosts = weekDays.map(day => getPostsForTimeSlot(day, hour));
              const hasAnyPosts = currentRowPosts.some(posts => posts.length > 0);

              return (
                <div key={hour} className={`mobile-calendar-time-row ${hasAnyPosts ? 'has-posts' : ''}`}>
                  <div className="mobile-calendar-time-label">
                    <span className="mobile-calendar-hour">{hour === 24 ? '00' : hour.toString().padStart(2, '0')}:00</span>
                  </div>

                  <div className="mobile-calendar-week-row-viewport">
                    <div
                      className="mobile-calendar-row-track mobile-carousel-track"
                      style={trackStyle}
                    >
                      {renderRowCells(previousWeekDays, hour, `previous-${hour}`)}
                      {renderRowCells(weekDays, hour, `current-${hour}`, currentRowPosts)}
                      {renderRowCells(nextWeekDays, hour, `next-${hour}`)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
      </div>

      {/* Campaign Selector Modal */}
      <MobileCampaignSelector
        campaigns={apiCampaigns}
        mode="modal"
        selectionMode="multiple"
        selectedCampaignIds={selectedCampaignIds}
        onCampaignSelectionChange={handleCampaignSelectionChange}
        showAllOption={true}
        requireConfirmation={true}
        isOpen={showCampaignSelector}
        onClose={() => setShowCampaignSelector(false)}
      />

      {/* Date Picker Modal */}
      {showDatePicker && (
        <div className="date-picker-overlay" onClick={() => setShowDatePicker(false)}>
          <div className="date-picker-modal" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="date-picker-header">
              <button
                className="date-picker-nav-button"
                onClick={() => navigatePickerMonth(-1)}
              >
                <ArrowIcon direction="left" size={16} />
              </button>

              <div className="date-picker-month-title">
                {monthYearFormatter.format(pickerMonth)}
              </div>

              <button
                className="date-picker-nav-button"
                onClick={() => navigatePickerMonth(1)}
              >
                <ArrowIcon direction="right" size={16} />
              </button>
            </div>

            {/* Weekday labels */}
            <div className="date-picker-weekdays">
              {datePickerWeekdays.map((day, index) => (
                <div key={`${day}-${index}`} className="date-picker-weekday">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar grid */}
            <div className="date-picker-grid">
              {getDaysInMonth(pickerMonth).map((date, index) => {
                const isTodayDate = date && isToday(date);
                const isSelected = date && isDateInCurrentWeek(date);

                return (
                  <button
                    key={index}
                    className={`date-picker-day ${!date ? 'empty' : ''} ${isTodayDate ? 'today' : ''} ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleDateSelect(date)}
                    disabled={!date}
                  >
                    {date ? date.getDate() : ''}
                  </button>
                );
              })}
            </div>

            {/* Quick actions */}
            <div className="date-picker-actions">
              <button
                className="date-picker-action-button"
                onClick={() => {
                  handleDateSelect(new Date());
                }}
              >
                {t('calendar.actions.goToday')}
              </button>
              <button
                className="date-picker-action-button secondary"
                onClick={() => setShowDatePicker(false)}
              >
                {t('calendar.actions.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileCalendar;
